import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const highlights = [
  { label: "Years Experience", value: 20, suffix: "+" },
  { label: "Employees Led", value: 800, suffix: "+" },
  { label: "Revenue Managed", value: "$30M", suffix: "+" },
  { label: "Human Capital Dev", value: "Expert", isText: true },
  { label: "Business Transformation", value: "Leader", isText: true },
  { label: "Education & Training", value: "Specialist", isText: true }
];

const expertise = [
  "Strategic Leadership", "Human Resource Management", "Talent Acquisition & Retention",
  "Learning & Development Strategy", "Business Unit Management", "Workforce Development",
  "Curriculum Design & Development", "Performance Management", "Organisational Transformation",
  "Business Continuity Planning", "Risk Management", "Compliance & Governance",
  "Sustainability Planning", "Stakeholder Engagement", "Operational Excellence",
  "Leadership Development", "Quality Assurance", "Academic Management",
  "Corporate Training", "Change Management"
];

const industryExperience = [
  "Higher Education", "Corporate Training", "Human Resources", 
  "Security & Aviation", "Hospitality & Tourism", "Government & Public Sector", 
  "Workforce Skills Qualifications (WSQ)", "Facilities Management", 
  "Business Consultancy", "Sustainability & Compliance"
];

const certifications = [
  "Bachelor of Laws (LL.B Honours)", "Bachelor of Business Administration", 
  "ACTA Certified Trainer", "Business Continuity Certified Planner (BCM 5000)", 
  "Professional Diploma in Digital Marketing", "Agile Project Management Certified", 
  "ISO 9001 / 14001 / 45001 Internal Auditor", "People Developer Standards Assessor",
  "Security Risk Assessment & Audit Certified", "Data Protection Management Certified",
  "Cisco Certified Network Associate (CCNA)", "WSH bizSAFE Certified",
  "Advanced Business Management Qualifications"
];

const impactSummary = [
  {
    title: "Strategic Business Leadership",
    desc: "Successfully led large-scale business units and multidisciplinary teams, driving operational performance, organisational growth, and sustainable business outcomes."
  },
  {
    title: "Human Capital & Workforce",
    desc: "Designed and implemented talent development, recruitment, retention, and workforce capability strategies that enhanced organisational effectiveness."
  },
  {
    title: "Education & Training Excellence",
    desc: "Developed and managed professional training programmes, WSQ frameworks, academic curricula, and higher education initiatives."
  },
  {
    title: "Governance, Risk & Compliance",
    desc: "Established and strengthened governance frameworks through business continuity planning, risk management, regulatory compliance, and operational controls."
  },
  {
    title: "Organisational Transformation",
    desc: "Partnered with public and private sector stakeholders to implement strategic initiatives that improved operational efficiency and resilience."
  },
  {
    title: "Leadership & Capability Building",
    desc: "Mentored leaders, managers, and professionals across multiple sectors, fostering strong leadership pipelines and high-performance workplace cultures."
  }
];

const DirectorProfile = () => {
  return (
    <section className="py-24 md:py-32 bg-brand-black relative overflow-hidden border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          
          {/* LEFT SIDE: Portrait Area */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 relative group"
          >
            <div className="relative z-10 border border-white/10 p-4 bg-white/5 backdrop-blur-sm shadow-2xl overflow-hidden">
              <img 
                src="https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780935705/PHOTO-2026-06-08-21-20-42_yk9qln.jpg" 
                alt="Jason Lim - Associate Director" 
                className="w-full aspect-[4/5] object-cover object-top grayscale brightness-90 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700"
              />
              <div className="absolute top-0 right-0 w-24 h-24 border-t-2 border-r-2 border-brand-gold -translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 z-20 bg-brand-gold p-6 shadow-xl">
              <p className="text-white text-xs font-black tracking-widest uppercase mb-1">Associate Director</p>
              <h3 className="text-white text-2xl font-bold tracking-tight">Jason Lim</h3>
            </div>
          </motion.div>

          {/* RIGHT SIDE: Executive Bio */}
          <div className="lg:col-span-7 pt-10 lg:pt-0">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-1 bg-brand-gold/10 border border-brand-gold/20 text-brand-gold text-[10px] font-bold tracking-widest uppercase mb-6">
                Leadership Profile
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-white mb-8 leading-tight">
                Driving Organisational Growth Through <br /> 
                <span className="text-brand-gold italic">Strategic Leadership</span>
              </h2>
              <div className="space-y-6 text-gray-400 font-light leading-relaxed text-lg max-w-3xl">
                <p>
                  Jason Lim is the Associate Director of White Knight Global, bringing over 20 years of leadership experience across human resources, workforce development, higher education, security management, and organisational transformation.
                </p>
                <p>
                  Throughout his distinguished career, Jason has successfully led large-scale business units, training academies, and workforce development programmes, managing teams of up to 800 employees and overseeing multimillion-dollar operations.
                </p>
                <p>
                  With extensive experience collaborating with government agencies and corporate organisations, Jason specialises in helping organisations strengthen workforce capabilities, enhance governance frameworks, and drive sustainable growth.
                </p>
              </div>

              {/* Highlight Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12">
                {highlights.map((item, idx) => (
                  <div key={idx} className="p-4 bg-white/5 border border-white/10 text-center hover:border-brand-gold/30 transition-colors">
                    <div className="text-2xl font-bold text-white mb-1">
                      {item.isText ? item.value : <span className="text-brand-gold">{item.value}{item.suffix}</span>}
                    </div>
                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-tight">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Expertise & Industry Experience */}
        <div className="mt-32 grid lg:grid-cols-2 gap-16">
          <div>
            <h3 className="text-2xl font-bold text-white tracking-tight mb-8 flex items-center gap-4">
              <span className="w-8 h-[1px] bg-brand-gold"></span>
              Areas of Expertise
            </h3>
            <div className="flex flex-wrap gap-2">
              {expertise.map((skill, idx) => (
                <span key={idx} className="px-4 py-2 bg-white/5 border border-white/5 text-gray-400 text-xs font-medium hover:border-brand-gold/50 transition-all cursor-default">
                  {skill}
                </span>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white tracking-tight mb-8 flex items-center gap-4">
              <span className="w-8 h-[1px] bg-brand-gold"></span>
              Industry Expertise
            </h3>
            <div className="flex flex-wrap gap-2">
              {industryExperience.map((industry, idx) => (
                <span key={idx} className="px-4 py-2 bg-brand-gold/5 border border-brand-gold/10 text-brand-gold text-xs font-medium cursor-default">
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Impact Summary Grid */}
        <div className="mt-24">
          <div className="text-center mb-12">
             <h3 className="text-3xl font-bold text-white tracking-tight">Executive Impact Summary</h3>
             <div className="w-16 h-1 bg-brand-gold mx-auto mt-4"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {impactSummary.map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 bg-brand-gray/5 border border-white/5 hover:bg-brand-gray/10 transition-all group"
              >
                <h4 className="text-brand-gold font-bold text-sm uppercase tracking-widest mb-4">{item.title}</h4>
                <p className="text-gray-400 text-xs leading-relaxed font-light">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Certifications & Quote */}
        <div className="mt-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-10 bg-white border border-brand-gold/20 text-brand-black relative order-2 lg:order-1"
          >
            <SafeIcon name="Quote" className="text-4xl text-brand-gold/20 mb-6" />
            <p className="text-xl font-light italic leading-relaxed mb-8">
              "Jason has successfully led business units, educational institutions, workforce development initiatives, and strategic transformation projects. His expertise enables organisations to strengthen performance and achieve sustainable long-term success."
            </p>
            <div className="flex items-center gap-4">
              <div className="w-10 h-[1px] bg-brand-gold"></div>
              <span className="text-xs font-bold uppercase tracking-widest">Professional Impact Statement</span>
            </div>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-4 order-1 lg:order-2">
            {certifications.map((cert, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4 bg-white/5 border border-white/5">
                <SafeIcon name="Check" className="text-brand-gold text-sm shrink-0" />
                <span className="text-xs text-gray-300 font-medium">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DirectorProfile;